import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridApi, GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IntakeQuoteLineItem, intakeQuoteLineItemArraySchema } from '../../IntakeQuoteStep.schema';
import { IntakeQuoteStepLineItemGridField } from '../../IntakeQuoteStep.types';
import {
  IntakeQuoteStepLineItemForm,
  LineItemDefaultValues,
} from '../IntakeQuoteStepLineItemForm/IntakeQuoteStepLineItemForm';
import { I18nNamespace } from '~/common/types';
import { GridRowClickAwayListener } from '~/routes/IntakeDetail/components/GridRowClickAwayListener/GridRowClickAwayListener';
import { IntakeDataGrid } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid';
import { useGridValidationStyling } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeDrawer } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawer';
import { IntakeDrawerContent } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerContent';
import { IntakeDrawerTitle } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerTitle';

import { useIntakeQuoteLineItemsGridConfig, useLineItemsGridHandlers } from './IntakeQuoteStepLineItems.dataGrid';

type IntakeQuoteStepLineItemsProps = {
  disabled?: boolean;
  apiRef: MutableRefObject<GridApi>;
  lineItems: IntakeQuoteLineItem[];
  lineItemFormDefaultValues: LineItemDefaultValues;
};

export const IntakeQuoteStepLineItems = ({
  disabled,
  apiRef,
  lineItems,
  lineItemFormDefaultValues,
}: IntakeQuoteStepLineItemsProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteLineItemStepForm' });

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      return rowIds?.findIndex((rId) => rId === rowId);
    },
    [apiRef],
  );

  const { getRowClassName, getGridClassName } = useGridValidationStyling(apiRef, intakeQuoteLineItemArraySchema);

  const {
    validate,
    currentSelectedRowId,
    setRowSelectionModel,
    selectedLineItem,
    handleLineItemChange,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
  } = useLineItemsGridHandlers(apiRef, lineItemFormDefaultValues);

  const config = useIntakeQuoteLineItemsGridConfig({ getRowIndex, onRowDelete: handleRowDelete, disabled });

  // enforce initial selection state and avoid error coloring
  // temporary fix until proper table validation styling is in place
  useEffect(() => {
    setRowSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  const rowSelectionModel = currentSelectedRowId ? [currentSelectedRowId] : [];

  return (
    <>
      <GridRowClickAwayListener onClickAway={handleCloseDrawer}>
        <IntakeDataGrid
          {...config}
          apiRef={apiRef}
          rows={lineItems}
          getRowHeight={() => 'auto'}
          initialState={{
            pinnedColumns: { right: [IntakeQuoteStepLineItemGridField.DELETE_ACTION] },
          }}
          editMode="row"
          columns={config.columns as unknown as GridColDef<(typeof lineItems)[number]>[]}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
          sortingMode="server"
          getRowClassName={({ id, row }) => {
            return id !== currentSelectedRowId || validate ? getRowClassName(row) : '';
          }}
          className={getGridClassName()}
        />
        <IntakeDrawer isOpen={!!selectedLineItem} handleCloseDrawer={handleCloseDrawer}>
          <IntakeDrawerTitle title={t('header')} handleCloseDrawer={handleCloseDrawer} />
          <IntakeDrawerContent>
            {selectedLineItem && (
              <IntakeQuoteStepLineItemForm
                key={selectedLineItem.id}
                lineItem={selectedLineItem}
                validate={validate}
                onChange={handleLineItemChange}
                disabled={disabled}
              />
            )}
          </IntakeDrawerContent>
        </IntakeDrawer>
      </GridRowClickAwayListener>
      <Box
        sx={{
          mt: 3,
        }}
      >
        <Button
          disabled={disabled}
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleAddRow();
          }}
          startIcon={<Add />}
        >
          {t('addLineItemButton')}
        </Button>
      </Box>
    </>
  );
};
